import { Link, useMatchRoute } from "@tanstack/react-router"
import {
  CreditCard,
  FileText,
  Grid3x3,
  LayoutDashboard,
  Send,
} from "lucide-react"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/ui/components/ui/sidebar"
import { useT } from "@/i18n"
import { useGetCurrentTenantQuery } from "@/tenant"

type Props = {
  readonly tenantSlug: string
}

/**
 * Per-tenant navigation (product.md §10.5). Team lives under Settings for now;
 * Billing is Owner-only (billingAdmin). Settings stays in the sidebar footer.
 */
export default function TenantNav({ tenantSlug }: Props) {
  const { t } = useT("common")
  const matchRoute = useMatchRoute()
  const { data: tenant } = useGetCurrentTenantQuery()

  const items = [
    {
      to: "/t/$tenantSlug/dashboard",
      icon: <LayoutDashboard />,
      label: t("nav.dashboard"),
    },
    {
      to: "/t/$tenantSlug/assignments",
      icon: <Send />,
      label: t("nav.assignments"),
    },
    {
      to: "/t/$tenantSlug/briefs",
      icon: <FileText />,
      label: t("nav.briefs"),
    },
    {
      to: "/t/$tenantSlug/evaluations",
      icon: <Grid3x3 />,
      label: t("nav.evaluations"),
    },
  ] as const

  return (
    <SidebarMenu>
      {items.map(({ to, icon, label }) => (
        <SidebarMenuItem key={to}>
          <SidebarMenuButton
            isActive={!!matchRoute({ to, fuzzy: true })}
            render={
              <Link to={to} params={{ tenantSlug }}>
                {icon}
                {label}
              </Link>
            }
          />
        </SidebarMenuItem>
      ))}
      {tenant?.permissions.billingAdmin && (
        <SidebarMenuItem>
          <SidebarMenuButton
            isActive={
              !!matchRoute({ to: "/t/$tenantSlug/billing", fuzzy: true })
            }
            render={
              <Link to="/t/$tenantSlug/billing" params={{ tenantSlug }}>
                <CreditCard />
                {t("nav.billing")}
              </Link>
            }
          />
        </SidebarMenuItem>
      )}
    </SidebarMenu>
  )
}
